import { ReadyState, Store } from './store';
import { BaseStore } from './baseStore';
import { action, makeObservable, observable, makeAutoObservable } from 'mobx';

export enum ThemeStoreState {
  NOT_INITIALIZED = 1 << 0,
  THEME_LOADED = 1 << 1,
}

export interface ThemeStyle {
  inputModernBorder: string;
  inputModernFocus: string;
  inputModernHover: string;
  inputModernBackground: string;
  inputModernText: string;
  main: string; // główne tło aplikacji
  menu: string; // kolor menu
  modal: string; // kolor modala
  sidebar: string; // sidebar
  secondary: string; //linia sidebar oraz header
  secondary70: string; // półprzezroczysty kolor dla sidebaru
  secondary50: string; // półprzezroczysty kolor dla sidebaru
  secondary8: string; // bardzo półprzezroczysty kolor dla przyciskow
  secondary20: string; // lekko półprzezroczysty kolor dla przycisków
  card: string; // panele i boxy
  cardMuted: string; // półprzezroczyste panele
  text: string; // tekst główny
  textSecondary: string; // tekst dodatkowy
  textMuted: string; // tekst pomocniczy
  textDateNotification: string; // tekst daty powiadomień
  border: string; // linie podziału
  icon: string; // ikony
  iconHover: string; // ikonka po najechaniu
  iconActive: string; // np. błyskawica, głośnik
  iconActiveRGB: string; // RGB dla aktywnej ikonki
  statusOnline: string;
  statusOnlineBg: string;
  statusOnlineHover: string;
  statusOffline: string;
  statusOfflineBg: string;
  statusOfflineHover: string;
  statusAway: string;
  statusAwayBg: string;
  statusAwayHover: string;
  statusBusy: string;
  statusBusyBg: string;
  statusBusyHover: string;
  statusDnd: string;
  statusDndBg: string;
  statusDndHover: string;
  statusUnknown: string;
  statusUnknownBg: string;
  statusUnknownHover: string;
  buttonPrimary: string; // kolor przycisku głównego
  buttonPrimaryHover: string; // kolor przycisku głównego po najechaniu
  buttonSecondary: string; // przycisk pomocniczy (np. szary)
  buttonSecondaryHover: string; // kolor po najechaniu na pomocniczy
  buttonDanger: string; // kolor przycisku niebezpiecznego (usuń)
  buttonDangerHover: string; // kolor hover przycisku niebezpiecznego
  buttonSuccess: string; // zielony przycisk typu "Akceptuj"
  buttonSuccessHover: string; // ciemniejszy zielony po najechaniu
  inputBackground: string; // = card
  inputBorder: string; // = border
  inputText: string; // = text
  inputPlaceholder: string; // = textMuted
  inputFocusBorder: string; // = buttonPrimary
  inputDisabledBackground: string;
  inputDisabledText: string;
  tooltipBackground: string; // tło dla tooltipów
  confirmationCancelText: string;
  confirmationCancelTextHover: string;
  confirmationAcceptButton: string;
  confirmationAcceptButtonHover: string;
  cancelBtnModal: string; // tło modala
  cancelBtnModalHover: string; // tło modala po najechaniu
}

export const darkTheme: ThemeStyle = {
  inputModernBorder: '#3457e35f',
  inputModernFocus: '#3458E3',
  inputModernHover: '#3457e3c3',
  inputModernBackground: 'rgba(255, 255, 255, 0.06)',
  inputModernText: '#fff',
  main: '#0F0F0F',
  menu: '#111215',
  modal: '#0d0d12',
  sidebar: '#0F0F0F',
  secondary: '52, 88, 227',
  secondary70: 'rgba(52, 88, 227, 0.5)',
  secondary50: 'rgba(52, 88, 227, 0.5)',
  secondary8: 'rgba(52, 88, 227, 0.08)',
  secondary20: 'rgba(52, 88, 227, 0.2)',
  card: '#1a1b2e',
  cardMuted: 'rgba(0,0,0,0.2)',
  text: '#F0F0F0',
  textSecondary: '#D9D9D9',
  textMuted: '#9ca3af',
  textDateNotification: '#9B9B9B',
  border: '#4b5563',
  icon: 'hsla(0, 0%, 54%, 0.5)',
  iconHover: '#8a8a8a',
  iconActive: '#3458E3',
  iconActiveRGB: '52, 88, 227',
  // ONLINE – zielony
  statusOnline: '#22c55e',
  statusOnlineBg: 'rgba(34, 197, 94, 0.08)', // lekki zielony przezroczysty
  statusOnlineHover: 'rgba(34, 197, 94, 0.2)', // ciemniejszy przy hoverze

  // OFFLINE – neutralny szary
  statusOffline: '#6b7280',
  statusOfflineBg: 'rgba(107, 114, 128, 0.08)',
  statusOfflineHover: 'rgba(107, 114, 128, 0.2)',

  // AWAY – żółty (ostrożność)
  statusAway: '#facc15',
  statusAwayBg: 'rgba(250, 204, 21, 0.08)',
  statusAwayHover: 'rgba(250, 204, 21, 0.2)',

  // BUSY – pomarańczowy (zajęty)
  statusBusy: '#f97316',
  statusBusyBg: 'rgba(249, 115, 22, 0.08)',
  statusBusyHover: 'rgba(249, 115, 22, 0.2)',

  // DND – czerwony (blokada)
  statusDnd: '#ef4444',
  statusDndBg: 'rgba(239, 68, 68, 0.08)',
  statusDndHover: 'rgba(239, 68, 68, 0.2)',

  // UNKNOWN – jasny szary (neutralne info)
  statusUnknown: '#9ca3af',
  statusUnknownBg: 'rgba(156, 163, 175, 0.08)',
  statusUnknownHover: 'rgba(156, 163, 175, 0.2)',
  buttonPrimary: '#3b82f6',
  buttonPrimaryHover: '#2563eb',
  buttonSecondary: '#4b5563',
  buttonSecondaryHover: '#374151',
  buttonDanger: '#ef4444',
  buttonDangerHover: '#dc2626',
  buttonSuccess: '#22c55e',
  buttonSuccessHover: '#16a34a',
  inputBackground: '#1a1b2e',
  inputBorder: '#4b5563',
  inputText: '#ffffff',
  inputPlaceholder: '#ffffff',
  inputFocusBorder: '#3b82f6',
  inputDisabledBackground: 'rgba(255,255,255,0.05)',
  inputDisabledText: '#6b7280',
  tooltipBackground: '#252B40', // tło dla tooltipów

  confirmationCancelText: '#ff5757',
  confirmationCancelTextHover: '#ff2121',
  confirmationAcceptButton: '#1a8629',
  confirmationAcceptButtonHover: '#339f41',

  cancelBtnModal: '#1E222D',
  cancelBtnModalHover: '#2D1E1E', // tło modala po najechaniu
};

export const lightTheme: ThemeStyle = {
  inputModernBorder: '#3457e35f',
  inputModernFocus: '#3458E3',
  inputModernHover: '#3457e3c3',
  inputModernBackground: 'rgba(0, 0, 0, 0.03)',
  inputModernText: '#222',
  main: '#f1f5f9',
  menu: '#141619',
  modal: '0d0d12',
  sidebar: '#ffffff',
  secondary: '#cbd5e1',
  secondary70: 'rgba(203, 213, 225, 0.7)',
  secondary50: 'rgba(203, 213, 225, 0.5)',
  secondary8: 'rgba(203, 213, 225, 0.08)',
  secondary20: 'rgba(203, 213, 225, 0.2)',
  card: '#f9fafb',
  cardMuted: '#c2c2c2',
  text: '#0f172a',
  textSecondary: '#1d2a49',
  textMuted: '#475569',
  textDateNotification: '#6B7280',
  border: '#d1d5db',
  icon: '#6b7280',
  iconHover: '#4b5563',
  iconActive: '#eab308',
  iconActiveRGB: '234, 180, 8',
  // ONLINE – zielony
  statusOnline: '#22c55e',
  statusOnlineBg: 'rgba(34, 197, 94, 0.08)', // lekki zielony przezroczysty
  statusOnlineHover: 'rgba(34, 197, 94, 0.2)', // ciemniejszy przy hoverze

  // OFFLINE – neutralny szary
  statusOffline: '#6b7280',
  statusOfflineBg: 'rgba(107, 114, 128, 0.08)',
  statusOfflineHover: 'rgba(107, 114, 128, 0.2)',

  // AWAY – żółty (ostrożność)
  statusAway: '#facc15',
  statusAwayBg: 'rgba(250, 204, 21, 0.08)',
  statusAwayHover: 'rgba(250, 204, 21, 0.2)',

  // BUSY – pomarańczowy (zajęty)
  statusBusy: '#f97316',
  statusBusyBg: 'rgba(249, 115, 22, 0.08)',
  statusBusyHover: 'rgba(249, 115, 22, 0.2)',

  // DND – czerwony (blokada)
  statusDnd: '#ef4444',
  statusDndBg: 'rgba(239, 68, 68, 0.08)',
  statusDndHover: 'rgba(239, 68, 68, 0.2)',

  // UNKNOWN – jasny szary (neutralne info)
  statusUnknown: '#9ca3af',
  statusUnknownBg: 'rgba(156, 163, 175, 0.08)',
  statusUnknownHover: 'rgba(156, 163, 175, 0.2)',
  buttonPrimary: '#3b82f6',
  buttonPrimaryHover: '#2563eb',
  buttonSecondary: '#94a3b8',
  buttonSecondaryHover: '#64748b',
  buttonDanger: '#ef4444',
  buttonDangerHover: '#dc2626',
  buttonSuccess: '#16a34a',
  buttonSuccessHover: '#15803d',
  inputBackground: '',
  inputBorder: '',
  inputText: '',
  inputPlaceholder: '',
  inputFocusBorder: '',
  inputDisabledBackground: '',
  inputDisabledText: '',
  tooltipBackground: '#f3f4f6', // tło dla tooltipów

  // ConfirmationBar colors
  confirmationCancelText: '#ff5757',
  confirmationCancelTextHover: '#ff2121',
  confirmationAcceptButton: '#1a8629',
  confirmationAcceptButtonHover: '#339f41',

  cancelBtnModal: '#f9fafb', // tło modala
  cancelBtnModalHover: '#2D1E1E', // tło modala
};

export class Theme {
  theme: string;
  wallpaper?: string;
  style: ThemeStyle;

  constructor(theme: string) {
    this.theme = theme;
    this.wallpaper = undefined;
    this.style = darkTheme;
    makeAutoObservable(this);
  }

  useHex(hex: string): string {
    return hex;
  }
  updateStyle<K extends keyof ThemeStyle>(key: K, value: ThemeStyle[K]) {
    this.style[key] = value;
  }

  setStyle(theme: ThemeStyle) {
    this.style = theme;
  }
}

export class ThemeStore extends BaseStore<ThemeStoreState> {
  theme: Theme | undefined | null;
  constructor(store: Store) {
    super(ThemeStoreState.NOT_INITIALIZED, store, 'green');
    makeObservable(this, {
      theme: observable,
      setTheme: action,
    });
    if (localStorage.getItem('initTheme') == null) {
      this.reset();
      this.log('Finished initializing theme by default');
      return;
    }
    this.reload();
  }

  reset() {
    this.theme = new Theme('default');
    this.reInsert();
    localStorage.setItem('theme', JSON.stringify(this.theme));
    localStorage.setItem('initTheme', 'true');
  }

  reload() {
    this.log('Starting init theme from cache');
    const themeData = Object.assign(
      new Theme('default'),
      JSON.parse(localStorage.getItem('theme')!),
    );
    this.theme = themeData;
    this.reInsert();
    this.setState(ThemeStoreState.THEME_LOADED);
    this.store!.addState(ReadyState.THEME);
  }

  setTheme(theme: Theme) {
    this.theme = theme;
  }

  reInsert() {
    Object.keys(this.theme.style).forEach(key => {
      const styleKey = key as keyof ThemeStyle;
      this.log(`Init style ${styleKey} with ${this.theme!.style[styleKey]}`);
      document.documentElement.style.setProperty(`--${styleKey}`, this.theme!.style[styleKey]);
    });
  }

  isReady(): boolean {
    return this.hasState(ThemeStoreState.THEME_LOADED);
  }
  logout(): void {
    this.setState(ThemeStoreState.NOT_INITIALIZED);
  }
}
